import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, take } from 'rxjs';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ScheduleCorrectionRequestsService } from 'src/app/services/api/schedule-correction-requests/schedule-correction-requests.service';
import { SubjectScheduleService } from 'src/app/services/api/subject-schedules/subject-schedule.service';
import { SubjectService } from 'src/app/services/api/subjects/subject.service';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import {
  StudyModeModel,
  SubjectScheduleGroupListParams,
  SubjectScheduleGroupListResponse,
  SubjectScheduleGroupSummary,
} from 'src/app/services/models/subject-schedules/subject-schedule.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  ScheduleCorrectionActiveNote,
  ScheduleCorrectionActiveNoteLifecycleStatus,
  ScheduleCorrectionActiveNoteParams,
} from 'src/app/services/models/schedule/schedule-correction-requests.model';
import { CurriculumType, SubjectManagementDataSource, SubjectManagementTab, SubjectManagementRow } from 'src/app/services/models/subjects/subject.model';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';
import { SUBJECT_MANAGEMENT_EDIT_ROLE_IDS } from 'src/utils/enum/role.enum';
import { mapSubjectManagementOverallStatus } from '../subject-management-status.mapper';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};

interface SubjectManagementSubjectTypeOption {
  id: number;
  label: string;
}

interface SubjectManagementCurriculumOption {
  id: number;
  label: string;
}

type SubjectManagementPageTab = 'subjects' | 'new_course_notes';
type ActiveNewCourseNoteStatusTab = 'all' | ScheduleCorrectionActiveNoteLifecycleStatus;

interface ActiveNewCourseNoteRow {
  sequence: number;
  teacherName: string;
  semester: string;
  academicYear: string;
  requestedAt: string;
  status: ScheduleCorrectionActiveNoteLifecycleStatus;
  statusLabel: string;
  reference: string;
  source: ScheduleCorrectionActiveNote;
}

interface ActiveNewCourseNoteDataSource {
  data: ActiveNewCourseNoteRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-subject-management',
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './subject-management.component.html',
  styleUrl: './subject-management.component.scss',
})
export class SubjectManagementComponent implements OnInit, OnDestroy {
  @ViewChild('manualStatusTemplate', { static: true })
  manualStatusTemplate!: TemplateRef<any>;
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;
  @ViewChild('activeNoteStatusTemplate', { static: true })
  activeNoteStatusTemplate!: TemplateRef<any>;
  @ViewChild('activeNoteActionTemplate', { static: true })
  activeNoteActionTemplate!: TemplateRef<any>;
  form!: FormGroup;
  columns: any[] = [];
  activeNoteColumns: any[] = [];
  datasource = signal<SubjectManagementDataSource>(this.createDataSource([]));
  activeNoteDatasource = signal<ActiveNewCourseNoteDataSource>({
    data: [],
    page: 1,
    limit: 10,
    offset: 0,
    total: 0,
    total_page: 1,
  });
  tableLoading = signal(true);
  activeNoteLoading = signal(false);
  selectedPageTab: SubjectManagementPageTab = 'subjects';
  selectedTab: SubjectManagementTab = 'all';
  activeNoteStatusTab: ActiveNewCourseNoteStatusTab = 'all';
  selectedActiveNote = signal<ScheduleCorrectionActiveNote | null>(null);
  pageSizeOptions = [10, 20, 50];
  readonly activeNoteModalId = 'subjectManagementActiveNoteModal';

  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  subjectTypeOptions: SubjectManagementSubjectTypeOption[] = [];
  curriculumOptions: SubjectManagementCurriculumOption[] = [];
  private academicTerms = signal<AcademicTermYearModel[]>([]);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private studyPeriodService = inject(StudyPeriodService);
  private correctionRequestsService = inject(ScheduleCorrectionRequestsService);
  private subjectScheduleService = inject(SubjectScheduleService);
  private subjectService = inject(SubjectService);
  private authService = inject(AuthService);

  private currentPage = 1;
  private currentLimit = 10;
  private activeNotePage = 1;
  private activeNoteLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private filterSubscription?: Subscription;
  private filterOptionsSubscription?: Subscription;
  private offeringLoadSubscription?: Subscription;
  private activeNoteLoadSubscription?: Subscription;
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    academicTerm: 'academic_term_name',
    year: 'academic_term_year',
    subjectType: 'study_mode_id',
    code: 'code',
    name: 'name',
    curriculum: 'curriculum_type_id',
  };

  get isSubjectManagementViewOnly(): boolean {
    const activeRoleId = this.authService.getActiveRoleId();

    return activeRoleId == null ||
      !SUBJECT_MANAGEMENT_EDIT_ROLE_IDS.some((roleId) => roleId === activeRoleId);
  }

  // ENOT hotfix (release 1): the add-subject button is enabled only when the
  // SELECTED term+year (filter) has been synced from REG — not the session's
  // active term. Reads the academicTerms signal so the button updates when the
  // term list arrives async and whenever the filter changes.
  get isSelectedTermSynced(): boolean {
    const { academicTerm, year } = this.form.getRawValue();
    return this.findSelectedAcademicTerm(academicTerm, year)?.sync_at != null;
  }


  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.initActiveNoteColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.filterOptionsSubscription?.unsubscribe();
    this.offeringLoadSubscription?.unsubscribe();
    this.activeNoteLoadSubscription?.unsubscribe();
  }

  onSearch(): void {
    if (this.selectedPageTab === 'new_course_notes') {
      this.activeNotePage = 1;
      this.loadActiveNewCourseNotes();
      return;
    }

    this.currentPage = 1;
    this.loadSubjectSchedules();
  }

  onPageTabChange(tab: SubjectManagementPageTab): void {
    if (tab === this.selectedPageTab) {
      return;
    }

    this.selectedPageTab = tab;
    if (tab === 'new_course_notes') {
      this.activeNotePage = 1;
      this.loadActiveNewCourseNotes();
    } else {
      this.currentPage = 1;
      this.loadSubjectSchedules();
    }
  }

  onTabChange(tab: SubjectManagementTab): void {
    if (tab === this.selectedTab) {
      return;
    }

    this.selectedTab = tab;
    this.currentPage = 1;
    this.loadSubjectSchedules();
  }

  onActiveNoteStatusTabChange(tab: ActiveNewCourseNoteStatusTab): void {
    if (tab === this.activeNoteStatusTab) {
      return;
    }

    this.activeNoteStatusTab = tab;
    this.activeNotePage = 1;
    this.loadActiveNewCourseNotes();
  }

  onAddSubject(): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    void this.router.navigate(['/subject-management/create']);
  }

  onPageChange(page: number): void {
    if (this.selectedPageTab === 'new_course_notes') {
      if (page < 1 || page > this.activeNoteDatasource().total_page || page === this.activeNotePage) {
        return;
      }

      this.activeNotePage = page;
      this.loadActiveNewCourseNotes();
      return;
    }

    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    this.loadSubjectSchedules();
  }

  onLimitChange(limit: number): void {
    if (this.selectedPageTab === 'new_course_notes') {
      if (!Number.isFinite(limit) || limit === this.activeNoteLimit) {
        return;
      }

      this.activeNoteLimit = limit;
      this.activeNotePage = 1;
      this.loadActiveNewCourseNotes();
      return;
    }

    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.currentPage = 1;
    this.loadSubjectSchedules();
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.currentPage = 1;
    this.loadSubjectSchedules();
  }

  onEditSubject(row: SubjectManagementRow): void {
    const sourceTypes = row.source?.types ?? [];
    const sourceType = row.source?.type;
    const type = sourceType != null && sourceType !== ''
      ? sourceType
      : sourceTypes[0] ?? '';
    const offeringId = row.source?.offering_id;

    void this.router.navigate(['/subject-management/details'], {
      queryParams: {
        ...(offeringId != null ? { offering_id: offeringId } : { id: row.id }),
        type,
        types: sourceTypes.join(','),
      },
      state: { subjectManagementRow: row },
    });
  }

  onOpenActiveNote(row: ActiveNewCourseNoteRow): void {
    this.selectedActiveNote.set(row.source);
    const modalElement = document.getElementById(this.activeNoteModalId);

    if (modalElement != null) {
      bootstrap.Modal.getOrCreateInstance(modalElement, STATIC_MODAL_OPTIONS).show();
    }
  }

  onCloseActiveNote(): void {
    const modalElement = document.getElementById(this.activeNoteModalId);
    if (modalElement != null) {
      bootstrap.Modal.getInstance(modalElement)?.hide();
    }

    this.selectedActiveNote.set(null);
  }

  onOpenActiveNoteSchedule(): void {
    const scheduleSubmissionId = this.selectedActiveNote()?.schedule_submission_id;
    if (typeof scheduleSubmissionId !== 'number' || !Number.isInteger(scheduleSubmissionId) || scheduleSubmissionId <= 0) {
      return;
    }

    const urlTree = this.router.createUrlTree([
      '/schedule-submissions-management/schedule-submissions-details',
      scheduleSubmissionId,
    ]);
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  hasActiveNoteSchedule(): boolean {
    const scheduleSubmissionId = this.selectedActiveNote()?.schedule_submission_id;
    return typeof scheduleSubmissionId === 'number'
      && Number.isInteger(scheduleSubmissionId)
      && scheduleSubmissionId > 0;
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = defaultAcademicTerm === '' ? [] : [defaultAcademicTerm];
    this.yearOptions = defaultYear === '' ? [] : [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
      keyword: [''],
      curriculumType: [null],
      subjectType: [null],
    });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'manualStatus',
        name: '',
        sortable: false,
        cellTemplate: this.manualStatusTemplate,
        width: 24,
        minWidth: 24,
        maxWidth: 24,
      },
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
      },
      {
        prop: 'academicTerm',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: true,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
      },
         {
        prop: 'subjectType',
        name: 'ประเภทรายวิชา',
        sortable: false,
        width: 180,
        minWidth: 180,
      },
      {
        prop: 'code',
        name: 'รหัสวิชา',
        sortable: true,
        width: 150,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        prop: 'name',
        name: 'ชื่อรายวิชา',
        sortable: true,
        width: 150,
        minWidth: 150,
      },
   
      {
        prop: 'curriculum',
        name: 'หลักสูตร',
        sortable: false,
        width: 180,
        minWidth: 180,
      },
      {
        prop: 'status',
        name: 'สถานะ',
        sortable: false,
        cellTemplate: this.statusTemplate,
        cellClass: 'subject-management-status-cell',
        headerClass: 'subject-management-status-header',
        width: 150,
        minWidth: 150,
      },
      {
        prop: 'actions',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        cellClass: 'subject-management-action-cell',
        headerClass: 'subject-management-action-header',
        width: 96,
        minWidth: 96,
        maxWidth: 96,
      },
    ];
  }

  private initActiveNoteColumns(): void {
    this.activeNoteColumns = [
      { prop: 'sequence', name: 'ลำดับ', sortable: false, width: 72, minWidth: 72, maxWidth: 72 },
      { prop: 'semester', name: 'ภาคการศึกษา', sortable: false, width: 110, minWidth: 110 },
      { prop: 'academicYear', name: 'ปีการศึกษา', sortable: false, width: 100, minWidth: 100 },
      { prop: 'teacherName', name: 'ชื่อ - นามสกุล', sortable: false, minWidth: 260 },
      { prop: 'requestedAt', name: 'วันที่ส่งคำขอ', sortable: false, minWidth: 160 },
      {
        prop: 'statusLabel',
        name: 'สถานะ',
        sortable: false,
        cellTemplate: this.activeNoteStatusTemplate,
        cellClass: 'subject-management-status-cell',
        headerClass: 'subject-management-status-header',
        width: 130,
        minWidth: 130,
      },
      {
        prop: 'actions',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.activeNoteActionTemplate,
        cellClass: 'subject-management-action-cell',
        headerClass: 'subject-management-action-header',
        width: 96,
        minWidth: 96,
        maxWidth: 96,
      },
    ];
  }

  private listenFilters(): void {
    this.filterSubscription = this.form.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        if (this.selectedPageTab === 'new_course_notes') {
          this.activeNotePage = 1;
          this.loadActiveNewCourseNotes();
        } else {
          this.currentPage = 1;
          this.loadSubjectSchedules();
        }
      });
  }

  private loadFilterOptions(): void {
    this.filterOptionsSubscription?.unsubscribe();
    const subscriptions = new Subscription();
    this.filterOptionsSubscription = subscriptions;

    subscriptions.add(
      this.studyPeriodService.getAcademicTermYears().pipe(
        take(1),
        catchError(() => of([] as AcademicTermYearModel[])),
      ).subscribe((academicTerms) => {
        this.academicTerms.set(academicTerms);
        this.academicTermOptions = this.buildAcademicTermOptions(academicTerms);
        this.yearOptions = this.buildAcademicYearOptions(academicTerms);
        this.ensureAcademicTermFilterValues();
        this.loadSubjectSchedules();
      }),
    );

    subscriptions.add(forkJoin({
      studyModes: this.subjectScheduleService.getStudyModes().pipe(
        take(1),
        catchError(() => of([] as StudyModeModel[])),
      ),
      curriculumTypes: this.subjectService.getCurriculumTypes({ datatable: false }).pipe(
        take(1),
        catchError(() => of([] as CurriculumType[])),
      ),
    }).subscribe(({ studyModes, curriculumTypes }) => {
      this.subjectTypeOptions = studyModes
        .map((studyMode) => this.mapStudyModeToOption(studyMode))
        .filter((option): option is SubjectManagementSubjectTypeOption => option != null);
      this.curriculumOptions = this.buildCurriculumOptions(curriculumTypes);
    }));
  }

  private loadSubjectSchedules(): void {
    const params = this.buildListParams();

    this.offeringLoadSubscription?.unsubscribe();
    this.tableLoading.set(true);
    this.offeringLoadSubscription = this.subjectScheduleService.getSubjectScheduleGroups(params)
      .pipe(finalize(() => this.tableLoading.set(false)))
      .subscribe({
        next: (response) => {
          const rows = response.data.map((item) => this.mapGroupToRow(item));

          this.datasource.set(this.createDataSourceFromResponse(response, rows));
        },
        error: () => {
          this.datasource.set(this.createDataSourceFromResponse({
            data: [],
            page: this.currentPage,
            limit: this.currentLimit,
            total: 0,
            total_pages: 1,
          }, []));
        },
      });
  }

  private loadActiveNewCourseNotes(): void {
    const params = this.buildActiveNoteParams();

    this.activeNoteLoadSubscription?.unsubscribe();
    this.activeNoteLoading.set(true);
    this.activeNoteLoadSubscription = this.correctionRequestsService.getActiveNewCourseNotes(params)
      .pipe(finalize(() => this.activeNoteLoading.set(false)))
      .subscribe({
        next: (response) => {
          const rows = response.data.map((note, index) => this.mapActiveNoteToRow(
            note,
            (response.page - 1) * response.limit + index + 1,
          ));

          this.activeNotePage = response.page;
          this.activeNoteLimit = response.limit;
          this.activeNoteDatasource.set({
            data: rows,
            page: response.page,
            limit: response.limit,
            offset: (response.page - 1) * response.limit,
            total: response.total,
            total_page: Math.max(1, response.total_pages),
          });
        },
        error: () => {
          this.activeNoteDatasource.set(this.createActiveNoteDataSource());
        },
      });
  }

  private buildActiveNoteParams(): ScheduleCorrectionActiveNoteParams {
    const filters = this.form.getRawValue();
    const selectedAcademicTerm = this.findSelectedAcademicTerm(filters.academicTerm, filters.year);
    const query = String(filters.keyword ?? '').trim();
    const params: ScheduleCorrectionActiveNoteParams = {
      page: this.activeNotePage,
      limit: this.activeNoteLimit,
    };

    if (selectedAcademicTerm != null) {
      params.academic_term_id = selectedAcademicTerm.id;
    }
    if (query !== '') {
      params.query = query;
    }
    if (this.activeNoteStatusTab !== 'all') {
      params.status = this.activeNoteStatusTab;
    }

    return params;
  }

  private mapActiveNoteToRow(note: ScheduleCorrectionActiveNote, sequence: number): ActiveNewCourseNoteRow {
    return {
      sequence,
      teacherName: note.requested_by?.teacher_display_name || note.requested_by?.full_name || '-',
      semester: this.displayValue(note.semester ?? note.academic_term?.name),
      academicYear: this.displayYear(note.academic_year ?? note.academic_term?.year),
      requestedAt: this.formatDateTime(note.requested_at),
      status: note.status,
      statusLabel: this.activeNoteStatusLabel(note.status, note.status_label),
      reference: note.reference,
      source: note,
    };
  }

  activeNoteStatusLabel(
    status: ScheduleCorrectionActiveNoteLifecycleStatus,
    fallbackLabel: string,
  ): string {
    return status === 'completed' ? 'เพิ่ม/แก้ไขรายวิชาสำเร็จ' : fallbackLabel;
  }

  private createActiveNoteDataSource(): ActiveNewCourseNoteDataSource {
    return {
      data: [],
      page: this.activeNotePage,
      limit: this.activeNoteLimit,
      offset: 0,
      total: 0,
      total_page: 1,
    };
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  private buildListParams(): SubjectScheduleGroupListParams {
    const filters = this.form.getRawValue();
    const params: SubjectScheduleGroupListParams = {
      summary: true,
      datatable: false,
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    };
    const keyword = String(filters.keyword ?? '').trim();
    const curriculumTypeId = String(filters.curriculumType ?? '').trim();
    const studyModeId = String(filters.subjectType ?? '').trim();
    const selectedAcademicTerm = this.findSelectedAcademicTerm(
      filters.academicTerm,
      filters.year,
    );


    if (selectedAcademicTerm != null) {
      params['academic_term_id'] = selectedAcademicTerm.id;
    }

    if (keyword !== '') {
      params['keyword'] = keyword;
    }

    if (curriculumTypeId !== '') {
      params['curriculum_type_id'] = curriculumTypeId;
    }

    if (studyModeId !== '') {
      params['study_mode_id'] = studyModeId;
    }

    if (this.selectedTab !== 'all') {
      params['status'] = this.selectedTab;
    }

    return params;
  }

  private createDataSource(rows: SubjectManagementRow[]): SubjectManagementDataSource {
    const total = rows.length;
    const totalPage = Math.max(1, Math.ceil(total / this.currentLimit));

    if (this.currentPage > totalPage) {
      this.currentPage = totalPage;
    }

    const offset = (this.currentPage - 1) * this.currentLimit;

    return {
      data: rows.slice(offset, offset + this.currentLimit),
      limit: this.currentLimit,
      offset,
      page: this.currentPage,
      total,
      total_page: totalPage,
    };
  }

  private createDataSourceFromResponse(
    response: SubjectScheduleGroupListResponse,
    rows: SubjectManagementRow[],
  ): SubjectManagementDataSource {
    const limit = Number(response.limit || this.currentLimit);
    const page = Number(response.page || this.currentPage);
    const totalPage = Math.max(1, Number(response.total_pages || 1));

    this.currentPage = page;
    this.currentLimit = limit;

    return {
      data: rows,
      limit,
      offset: (page - 1) * limit,
      page,
      total: Number(response.total || rows.length),
      total_page: totalPage,
    };
  }

  private mapGroupToRow(item: SubjectScheduleGroupSummary): SubjectManagementRow {
    const status = mapSubjectManagementOverallStatus(
      item.approval_status?.status_code ?? item.status,
    );

    return {
      id: Number(item.subject_id ?? item.id),
      sequence: item.sequence,
      academicTerm: this.displayValue(item.academic_term?.name ?? item.academic_term_id),
      year: this.displayYear(item.academic_term?.year),
      code: this.displayValue(item.subject?.code),
      name: this.displayValue(item.subject?.name),
      subjectType: this.displayValue(item.study_mode?.name_th ?? item.study_mode?.name_en ?? item.study_mode?.code),
      curriculum: this.displayValue(item.curriculum_type?.name ?? item.subject?.curriculum_type?.name),
      isSynced: item.is_synced,
      status: status.status,
      statusLabel: status.label,
      source: item,
    };
  }

  displayYear(year: string | number | null | undefined): string {
    if (year === null || year === undefined || year === '') {
      return '-';
    }

    const parsedYear = Number(year);

    if (!Number.isFinite(parsedYear)) {
      return '-';
    }

    return String(parsedYear > 2400 ? parsedYear : parsedYear + 543);
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private buildCurriculumOptions(curriculumTypes: CurriculumType[]): SubjectManagementCurriculumOption[] {
    const optionsById = new Map<number, SubjectManagementCurriculumOption>();

    curriculumTypes.forEach((curriculumType) => {
      const label = curriculumType.name.trim();

      if (label === '' || optionsById.has(curriculumType.id)) {
        return;
      }

      optionsById.set(curriculumType.id, {
        id: curriculumType.id,
        label,
      });
    });

    return Array.from(optionsById.values());
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const currentYear = String(this.form.get('year')?.value ?? '');

    if (this.findSelectedAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms().length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms()[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    return String(this.authService.currentUser()?.academic_term?.name ?? '').trim();
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return '';
    }

    return String(this.toBuddhistYear(sessionYear));
  }

  private findSelectedAcademicTerm(
    academicTermName: unknown,
    academicYear: unknown,
  ): AcademicTermYearModel | undefined {
    const selectedName = this.toFilterString(academicTermName);
    const selectedYear = this.toFilterString(academicYear);

    if (selectedName === '' || selectedYear === '') {
      return undefined;
    }

    return this.academicTerms().find((term) => (
      String(term.name).trim() === selectedName
      && String(this.toBuddhistYear(term.year)) === selectedYear
    ));
  }

  private toFilterString(value: unknown): string {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value).trim();
    }

    return '';
  }

  private mapStudyModeToOption(studyMode: StudyModeModel): SubjectManagementSubjectTypeOption | null {
    const label = String(studyMode.name_th || studyMode.name_en || studyMode.code || '').trim();

    if (!label) {
      return null;
    }

    return {
      id: studyMode.id,
      label,
    };
  }

  private toBuddhistYear(year: unknown): number {
    const parsedYear = Number(year);

    if (!Number.isFinite(parsedYear)) {
      return Number.NaN;
    }

    return parsedYear > 2400 ? parsedYear : parsedYear + 543;
  }

  private displayValue(value: unknown): string {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return '-';
  }

}
